import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { map } from 'rxjs/operators';
import { getPluginForScope } from '../plugins/plugin.js';
import type { BHTranslationDictionary } from '../translations/translation.js';
import type { User } from './user.js';

// create indices
(async () => {
  if (!context?.serverContext) return;
  const stammdatenCollection = await getRawCollection('user-stammdaten');
  await stammdatenCollection.createIndex({ userID: 1, institutionID: 1 }, { unique: true });
})();

@Entity()
export class UserStammdaten extends MongoEntity {
  static override _class = 'UserStammdaten';
  static override _collectionName = 'user-stammdaten';

  static override get allFields() {
    return selectionSet(() => this, 3);
  }

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  override _id!: string;

  @validate(nonOptional())
  @Field(type => ID)
  userID!: string;

  @validate(nonOptional())
  @Field(type => String)
  institutionID: Maybe<string>;

  getTranslationNamespace(): Maybe<string> {
    return null;
  }

  static override async beforeCreate(entity: UserStammdaten): Promise<void> {
    super.beforeCreate(entity);

    const existing = await (
      await getCollection<UserStammdaten>(UserStammdaten._class)
    ).findOne({ userID: entity.userID, institutionID: entity.institutionID });
    if (existing) {
      throw new Error('user stammdaten needs to be unique across ');
    }
  }

  static getSelector(userID: string, institutionID: Maybe<string>) {
    return institutionID ? { userID, institutionID } : { userID, institutionID: null };
  }

  @Query(type => UserStammdaten)
  static async prefillBestEffort(
    @Arg('userID', type => String) userID: string,
    @Arg('institutionID', type => String) institutionID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<UserStammdaten>) => {
      const collection = await getCollection<UserStammdaten>(UserStammdaten._class);
      const institutionResult = institutionID ? await collection.findOne({ userID, institutionID }) : null;
      const userResult = await collection.findOne({ userID, institutionID: null });

      return constructValue({
        ...userResult,
        ...institutionResult,
        _id: undefined,
      }) as UserStammdaten;
    };
  }

  static createStammdatenForInstitutionID(
    @Arg('institutionID', type => String) institutionID: string | null,
    @Arg('createInDB', type => Boolean) createInDB: boolean = false,
  ) {
    const stammdatenPlugin = getPluginForScope('registration::stammdaten', institutionID ?? 'default');
    return stammdatenPlugin.createStammdaten({ institutionID, createInDB });
  }

  @Query(type => UserStammdaten)
  static getExistingStammdaten<TStammdaten extends UserStammdaten>(
    @Arg('userID', type => String) userID: string,
    @Arg('institutionID', type => String) institutionID: string,
    @Arg('id', type => String) id: Maybe<string>,
  ) {
    return async (gqlFields: BaseQLSelectionSet<TStammdaten>) => {
      const collection = await getCollection<UserStammdaten>(UserStammdaten._class);
      const existing = await collection.findOne<UserStammdaten>({
        $or: [
          {
            _id: id,
          },
          {
            userID,
            institutionID,
          },
        ],
      });
      return existing as Maybe<TStammdaten>;
    };
  }

  @Query(type => UserStammdaten)
  static getByUserAndInstitution<T extends UserStammdaten>(
    @Arg('userID', type => String) userID: string,
    @Arg('institutionID', type => String) institutionID: string | null,
  ) {
    return async <Tsel extends BaseQLSelectionSet<T>>(gqlFields: Tsel) => {
      const collection = await getCollection<T>(UserStammdaten._class);
      const result = await collection.findOne({ userID, institutionID });
      return result as NarrowedEntity<T, Tsel> | null;
    };
  }

  @Subscription(type => UserStammdaten)
  static subscribeByUserAndInstitution(
    @Arg('userID', type => String) userID: string,
    @Arg('institutionID', type => String) institutionID: string | null,
  ) {
    return (gqlFields: BaseQLSelectionSet<UserStammdaten>) => {
      return watchQuery<UserStammdaten>(UserStammdaten._collectionName, { userID, institutionID }).pipe(
        map(result => result[0]),
      );
    };
  }

  static async getAllowedUserStammdaten() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const { User } = await import('./user.js');
    const userIDs = await User.getAllowedUsers();

    const result = await (
      await getRawCollection(UserStammdaten._collectionName)
    )
      .aggregate<{ _id: string }>([
        {
          $match: {
            userID: {
              $in: userIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray();

    return result.map(res => res._id);
  }

  static getStammdatenFieldsForInstitution(institutionID: string) {
    const stammdatenPlugin = getPluginForScope('registration::stammdaten', institutionID);
    const fields = (() => {
      const stammdatenClass = stammdatenPlugin.classType();
      // add expception for base class to not get all fields of all inheriting classes
      if (stammdatenClass._class === 'UserStammdaten') return ['userID', 'institutionID', 'institution'];
      return Object.keys(stammdatenClass.allFields);
    })();

    return fields;
  }

  static getEnumTranslation(translationController: TranslationController<BHTranslationDictionary>, value: string) {
    if (
      value.startsWith('ICSRM_') ||
      value.startsWith('SD_') ||
      value.startsWith('TUDA_') ||
      value.startsWith('PBS_')
    ) {
      return translationController.translate('enums', value as any);
    }
  }
}
